import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import React from 'react';
import {
  AllianceIdentity,
  getRecruitTarget,
  getShortName,
} from './alliance-identity';
import { APP_BAR, STATUS_COLORS } from './alliance-tokens';
import {
  AvatarTile,
  BarButton,
  getInitials,
  useIsMobileLayout,
} from './bar-controls';
import { BarMenu, MENU_COLORS, MenuOverline } from './bar-menu';
import { CURRENT_APP } from './current-app';
import { appAvatarColors, RecruitLink } from './recruit-link';

export interface UserMenuAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const MONO = "'Roboto Mono', monospace";
const LIME = STATUS_COLORS.impersonating;
const ORANGE = STATUS_COLORS.staff;

/**
 * The user control in the App Bar and the menu it opens.
 *
 * Handles agent, admin (username, no NAA number), staff (orange, "for
 * NAA…"), and impersonation by an admin, an upline agent or staff (lime,
 * with an eye badge). Below md it collapses to the avatar tile and the menu
 * opens as a bottom sheet.
 *
 * @param identity - Normalized signed-in user (see alliance-identity.ts)
 * @param onSignOut - Clears the session and signs out of Cognito
 * @param onStopImpersonating - Shown as the first action while impersonating
 * @param actions - Extra actions above Sign out (ARC: Account)
 */
export function UserControl(props: {
  identity: AllianceIdentity;
  onSignOut: () => void;
  onStopImpersonating?: () => void;
  actions?: UserMenuAction[];
}) {
  // Props
  const { identity } = props;
  // State
  const [open, setOpen] = React.useState(false);
  // Hooks
  const isMobile = useIsMobileLayout();

  const { role, user, staffFor, impersonating } = identity;
  const isStaff = role === 'staff';
  const appAvatar = appAvatarColors();

  // Bar control: who is shown, and in which status color
  const shown = impersonating && !isStaff ? impersonating : user;
  const control = {
    initials: getInitials({ name: (impersonating || user).fullName }),
    line1: getShortName({ fullName: shown.fullName }),
    // An admin with no known name shows the username once, then the role
    line2: isStaff
      ? `for ${staffFor?.id || ''}`
      : shown.fullName === shown.id && role === 'admin' && !impersonating
        ? 'Admin'
        : shown.id,
    border: APP_BAR.controlBorder,
    background: 'transparent',
    avatarBg: appAvatar.background,
    avatarFg: appAvatar.color,
    line2Color: CURRENT_APP.accentOnBar,
  };
  if (isStaff) {
    Object.assign(control, {
      border: ORANGE.main,
      background: ORANGE.tint,
      avatarBg: ORANGE.main,
      avatarFg: ORANGE.onMain,
      line2Color: ORANGE.text,
    });
  }
  if (impersonating && !isStaff) {
    Object.assign(control, {
      border: LIME.main,
      background: LIME.tint,
      line2Color: LIME.main,
    });
  }
  if (impersonating) {
    Object.assign(control, { avatarBg: LIME.main, avatarFg: LIME.onMain });
  }
  if (CURRENT_APP.key === 'university' && (isStaff || impersonating)) {
    control.line2Color = '#ffffff';
  }

  const recruit = getRecruitTarget({ identity });

  const handleAction = (action: () => void) => {
    setOpen(false);
    action();
  };

  const actions: (UserMenuAction & { highlight?: boolean })[] = [];
  if (impersonating && props.onStopImpersonating) {
    actions.push({
      label: 'Stop impersonating',
      icon: <PersonOffIcon sx={{ fontSize: 18 }} />,
      onClick: props.onStopImpersonating,
      highlight: true,
    });
  }
  actions.push(...(props.actions || []));
  actions.push({
    label: 'Sign out',
    icon: <LogoutIcon sx={{ fontSize: 18 }} />,
    onClick: props.onSignOut,
  });

  const accessibleName = impersonating
    ? `Viewing as ${impersonating.fullName}, signed in as ${user.fullName}`
    : `Signed in as ${user.fullName}`;

  return (
    <React.Fragment>
      <BarButton
        open={open}
        aria-label={`${accessibleName}. Account menu`}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'alliance-user-menu' : undefined}
        onClick={() => setOpen((current) => !current)}
        sx={{
          gap: '10px',
          p: isMobile ? '0 5px' : '0 8px 0 5px',
          borderColor: control.border,
          backgroundColor: control.background,
          '&:hover': {
            backgroundColor:
              control.background === 'transparent'
                ? APP_BAR.controlHover
                : control.background,
          },
        }}
      >
        <AvatarTile
          initials={control.initials}
          size={APP_BAR.avatarSize}
          background={control.avatarBg}
          color={control.avatarFg}
        >
          {impersonating ? (
            <Box
              sx={{
                position: 'absolute',
                right: -5,
                bottom: -5,
                width: 16,
                height: 16,
                borderRadius: '8px',
                backgroundColor: LIME.onMain,
                color: LIME.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: `1.5px solid ${CURRENT_APP.bar}`,
              }}
            >
              <VisibilityIcon sx={{ fontSize: 10 }} />
            </Box>
          ) : null}
        </AvatarTile>

        {isMobile ? null : (
          <React.Fragment>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
                lineHeight: 1.1,
                textAlign: 'left',
              }}
            >
              <Box sx={{ fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap' }}>
                {control.line1}
              </Box>
              <Box
                sx={{
                  fontFamily: MONO,
                  fontSize: 11,
                  fontWeight: 700,
                  color: control.line2Color,
                  whiteSpace: 'nowrap',
                }}
              >
                {control.line2}
              </Box>
            </Box>
            {open ? (
              <ExpandLessIcon sx={{ fontSize: 18, color: '#ffffffb3' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 18, color: '#ffffffb3' }} />
            )}
          </React.Fragment>
        )}
      </BarButton>

      <BarMenu
        id="alliance-user-menu"
        label="Account menu"
        open={open}
        onClose={() => setOpen(false)}
        width={APP_BAR.userMenuWidth}
      >
        {impersonating && !isStaff ? (
          <ImpersonationHeader identity={identity} />
        ) : (
          <AccountHeader identity={identity} />
        )}

        {recruit ? (
          <RecruitLink agent={recruit.agent} relation={recruit.relation} />
        ) : null}

        <Box sx={{ height: '1px', backgroundColor: MENU_COLORS.divider }} />

        <Box
          sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: '2px' }}
        >
          {actions.map((action) => (
            <ButtonBase
              key={action.label}
              onClick={() => handleAction(action.onClick)}
              sx={{
                fontFamily: 'inherit',
                height: isMobile ? 52 : 44,
                justifyContent: 'flex-start',
                gap: '12px',
                px: 1,
                borderRadius: '10px',
                fontSize: 14,
                fontWeight: 500,
                '&:hover': { backgroundColor: MENU_COLORS.hover },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  backgroundColor: action.highlight
                    ? LIME.main
                    : MENU_COLORS.hover,
                  color: action.highlight ? LIME.onMain : MENU_COLORS.text2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {action.icon}
              </Box>
              {action.label}
            </ButtonBase>
          ))}
        </Box>
      </BarMenu>
    </React.Fragment>
  );
}

/**
 * Menu header while an admin or upline agent is impersonating: the lime
 * "Viewing as" card, then who is actually signed in.
 */
function ImpersonationHeader(props: { identity: AllianceIdentity }) {
  // Props
  const { user, impersonating, role } = props.identity;
  const appAvatar = appAvatarColors();

  if (!impersonating) {
    return null;
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          m: 1,
          p: '14px',
          borderRadius: '12px',
          backgroundColor: LIME.main,
          color: LIME.onMain,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.1em',
          }}
        >
          <VisibilityIcon sx={{ fontSize: 16 }} />
          VIEWING AS
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <AvatarTile
            initials={getInitials({ name: impersonating.fullName })}
            size={44}
            background={LIME.onMain}
            color={LIME.main}
          />
          <PersonLines
            name={impersonating.fullName}
            id={impersonating.id}
            email={impersonating.email}
            idColor="inherit"
            emailSx={{ opacity: 0.8 }}
          />
        </Box>
      </Box>

      <Box
        sx={{
          p: '6px 16px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <AvatarTile
          initials={getInitials({ name: user.fullName })}
          size={32}
          background={appAvatar.background}
          color={appAvatar.color}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '3px',
            minWidth: 0,
          }}
        >
          <MenuOverline>Signed in as</MenuOverline>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              columnGap: 1,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            {user.fullName}
            {user.id !== user.fullName ? (
              <Box
                component="span"
                sx={{
                  fontFamily: MONO,
                  fontSize: 12,
                  fontWeight: 700,
                  color: MENU_COLORS.accent,
                }}
              >
                {user.id}
              </Box>
            ) : null}
            <RoleChip label={role === 'agent' ? 'Upline agent' : 'Admin'} />
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  );
}

/**
 * Menu header for everyone else: the signed-in account, plus the orange
 * "Staff for" and lime "Viewing as" notes for staff.
 */
function AccountHeader(props: { identity: AllianceIdentity }) {
  // Props
  const { role, user, staffFor, impersonating } = props.identity;
  const appAvatar = appAvatarColors();
  const isStaff = role === 'staff';

  return (
    <React.Fragment>
      <Box sx={{ p: 2, display: 'flex', gap: '14px', alignItems: 'center' }}>
        <AvatarTile
          initials={getInitials({ name: user.fullName })}
          size={44}
          background={isStaff ? ORANGE.main : appAvatar.background}
          color={isStaff ? ORANGE.onMain : appAvatar.color}
        />
        <PersonLines
          name={user.fullName}
          id={user.id}
          email={user.email}
          idColor={MENU_COLORS.accent}
          chip={
            role === 'admin' ? (
              <RoleChip label="Admin" />
            ) : isStaff ? (
              <RoleChip label="Staff" filled />
            ) : null
          }
        />
      </Box>

      {isStaff && staffFor ? (
        <StatusNote
          icon={<SupportAgentIcon sx={{ fontSize: 18, color: '#FB8C00' }} />}
          sx={{
            backgroundColor: `${ORANGE.main}1f`,
            border: `1px solid ${ORANGE.main}80`,
          }}
          prefix="Staff for"
          name={staffFor.fullName !== staffFor.id ? staffFor.fullName : ''}
          id={staffFor.id}
        />
      ) : null}

      {isStaff && impersonating ? (
        <StatusNote
          icon={<VisibilityIcon sx={{ fontSize: 18 }} />}
          sx={{ backgroundColor: LIME.main, color: LIME.onMain }}
          prefix="Viewing as"
          name={impersonating.fullName}
          id={impersonating.id}
        />
      ) : null}
    </React.Fragment>
  );
}

/** Name, ID (mono) and email stacked */
function PersonLines(props: {
  name: string;
  id: string;
  email?: string | null;
  idColor: string;
  chip?: React.ReactNode;
  emailSx?: object;
}) {
  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ fontSize: 16, fontWeight: 500 }}>{props.name}</Box>
        {props.chip}
      </Box>
      {props.id !== props.name ? (
        <Box
          sx={{
            fontFamily: MONO,
            fontSize: 12,
            fontWeight: 700,
            color: props.idColor,
          }}
        >
          {props.id}
        </Box>
      ) : null}
      {props.email ? (
        <Box
          sx={{
            fontSize: 13,
            color: MENU_COLORS.text2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...props.emailSx,
          }}
        >
          {props.email}
        </Box>
      ) : null}
    </Box>
  );
}

/** Small uppercase role chip: outlined (Admin, Upline agent) or orange (Staff) */
function RoleChip(props: { label: string; filled?: boolean }) {
  return (
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        borderRadius: '6px',
        p: '2px 6px',
        whiteSpace: 'nowrap',
        border: `1px solid ${props.filled ? ORANGE.main : MENU_COLORS.divider}`,
        backgroundColor: props.filled ? ORANGE.main : 'transparent',
        color: props.filled ? ORANGE.onMain : MENU_COLORS.text2,
      }}
    >
      {props.label}
    </Box>
  );
}

/** One-line status note under the account header ("Staff for …") */
function StatusNote(props: {
  icon: React.ReactNode;
  sx: object;
  prefix: string;
  name: string;
  id: string;
}) {
  return (
    <Box
      sx={{
        m: '0 16px 12px',
        p: '10px 12px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: 13,
        ...props.sx,
      }}
    >
      {props.icon}
      <div>
        {props.prefix} {props.name ? <b>{props.name}</b> : null}{' '}
        <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700 }}>
          {props.id}
        </Box>
      </div>
    </Box>
  );
}
